import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import NuevaReferida from './pages/NuevaReferida';
import Invitados from './pages/Invitados';
import AdminUsers from './pages/AdminUsers';
import AdminMemberProfile from './pages/AdminMemberProfile';
import MiPerfil from './pages/MiPerfil';
import CambiarPassword from './pages/CambiarPassword';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import { getProfile, logout as apiLogout, getEventTypes } from './api/api';
import { mergeEventTypeLabels } from './components/Charts';
import { ModalHost } from './components/Modal';
import { ToastHost } from './components/Toast';
import { ensurePushSubscription } from './pushNotifications';

// Ruta /logout heredada de los tiempos de Auth0 (bookmarks, links viejos). Ya no es
// el flujo real de cierre de sesión (eso lo hace el botón del Sidebar), pero si alguien
// cae aquí, cerramos sesión igual en vez de dejar una pantalla en blanco.
function LogoutPage() {
  const navigate = useNavigate();
  useEffect(() => {
    apiLogout().catch(() => {}).finally(() => {
      navigate('/', { replace: true });
      window.location.reload();
    });
  }, [navigate]);
  return <div className="state-message"><p>Cerrando sesión...</p></div>;
}

function AppShell() {
  const [profile, setProfile] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    getProfile().then(res => setProfile(res.data)).catch(() => setProfile(null));
    // Catálogo de tipos de evento (Café HUB, Junta Mensual, y lo que Admin haya agregado) —
    // se trae una vez por sesión y se fusiona en el mapa de etiquetas que usa toda la app.
    getEventTypes().then(res => mergeEventTypeLabels(res.data)).catch(() => {});
  }, []);

  // Se pide el permiso de notificaciones push apenas la cuenta está activa — es lo más
  // cercano a "activadas por default" que permite un navegador: nunca se puede otorgar el
  // permiso en silencio, así que se dispara el diálogo del navegador solo, en vez de
  // esconder un botón de "activar notificaciones" que nadie encontraría.
  useEffect(() => {
    if (profile && profile.status === 'active') {
      ensurePushSubscription();
    }
  }, [profile]);

  const isAuthenticated = !!profile;

  // Si administración le asignó una contraseña temporal (ver AdminUsers > Miembros >
  // "Generar contraseñas temporales"), no puede tocar nada más del sitio hasta que la
  // cambie por una propia — ni siquiera Mi Perfil. Tiene prioridad sobre profileIncomplete.
  const mustChangePassword = isAuthenticated && !!profile.must_change_password;

  // Mientras falten datos de perfil o servicios, se obliga a completarlos antes de
  // usar cualquier otra parte del sitio — se pidió explícitamente que sea "sí o sí".
  const profileIncomplete = isAuthenticated && !(profile.profile_complete && profile.has_services);

  const gate = (element) => {
    if (mustChangePassword) return <Navigate to="/cambiar-password" replace />;
    if (profileIncomplete) return <Navigate to="/mi-perfil" replace />;
    return element;
  };

  const routes = (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/registro" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route
        path="/cambiar-password"
        element={
          !isAuthenticated ? <Navigate to="/" replace />
          : !mustChangePassword ? <Navigate to="/dashboard" replace />
          : <CambiarPassword onChanged={() => getProfile().then(res => setProfile(res.data)).catch(() => {})} />
        }
      />
      <Route path="/dashboard" element={gate(<Dashboard />)} />
      <Route path="/nueva-referida" element={gate(<NuevaReferida />)} />
      <Route path="/invitados" element={gate(<Invitados />)} />
      <Route
        path="/mi-perfil"
        element={mustChangePassword ? <Navigate to="/cambiar-password" replace /> : <MiPerfil onProfileSaved={(p) => setProfile(p)} />}
      />
      <Route path="/admin" element={gate(<AdminUsers />)} />
      <Route path="/admin/:section" element={gate(<AdminUsers />)} />
      <Route path="/admin/miembros/:userId" element={gate(<AdminMemberProfile />)} />
      <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />} />
    </Routes>
  );

  if (!isAuthenticated) {
    // Login/registro/landing son autónomos — traen su propio encabezado, no necesitan chrome de la app.
    return routes;
  }

  return (
    <div className="app-shell">
      <Sidebar profile={profile} mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <div className="app-content">
        <Topbar profile={profile} onMenuClick={() => setMobileNavOpen(true)} />
        <div className="app-content-body">{routes}</div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ModalHost />
      <ToastHost />
      <AppShell />
    </Router>
  );
}

export default App;
